import React from "react";

import { COLORS } from "manager/config/constants";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import Icon from "shared/components/Icon";
import { Popover } from "shared/components/Popover";
import Tooltip from "shared/components/Tooltip";
import { MediumTitle } from "shared/components/Typography";
import {
  COLORS as BaseColors,
  colors,
  typography,
} from "shared/config/constants";
import { Permission } from "shared/graphql";
import { useDeviceType } from "shared/hooks";

import { AppStatus } from "../types";

import { Actions, ButtonType } from "./interfaces";
import {
  ActionMainButton,
  MoreActionsButton,
  LgPriButton,
  LumenSublabel,
  Subtitle,
  EmptyBoxWhite,
  CalculatingWhite,
  PopoverContainer,
  StyledActionButton,
  CosignActionButtonContainer,
  CosignIconWrapper,
  CosignButtonContent,
  CosignButtonTitle,
  CosignButtonSubtitle,
  CosignExternalIcon,
  CosignLoader,
  CosignStatus,
} from "./styled";
import { useCancelCosignApplication } from "manager/hooks/api";

export const DoneTitle = ({ text }: { text: string }) => {
  return (
    <MediumTitle
      strong
      style={{ color: typography.textColors.dark.primary, alignSelf: "center" }}
    >
      {text?.toUpperCase()}
    </MediumTitle>
  );
};


const COSIGN_BUTTON_CONFIG: Record<CosignStatus, {
  title: string;
  subtitle?: string;
  showSparkle?: boolean;
}> = {
  available: {
    title: 'INITIATE COSIGN',
    subtitle: 'Get guaranteed coverage',
    showSparkle: true,
  },
  pending: {
    title: 'Cancel Cosign Application',
  },
  approved: {
    title: 'VIEW COSIGN GUARANTEE',
  },
  denied: {
    title: 'REAPPLY TO COSIGN',
    // subtitle: 'Submit new cosign application',
  },
  canceled: {
    title: 'REAPPLY TO COSIGN',
    // subtitle: 'Submit new cosign application',
  },
  expired: {
    title: 'REAPPLY TO COSIGN',
    // subtitle: 'Submit new cosign application',
  },
};

export const CosignActionButton: React.FC<{
  status?: CosignStatus;
  onClick?: () => void;
  disabled?: boolean;
  isCreatingCosignApplication?: boolean;
  lgPri?: boolean;
}> = ({ status = 'available', onClick, disabled = false, isCreatingCosignApplication = false, lgPri = false }) => {
  const config = COSIGN_BUTTON_CONFIG[status];

  return (
    <CosignActionButtonContainer status={status} onClick={onClick} disabled={disabled || isCreatingCosignApplication}>
      <CosignIconWrapper status={status}>
        <Icon.CosignShieldIcon fill="#fff" width={16} height={16} />
      </CosignIconWrapper>
      <CosignButtonContent>
        <CosignButtonTitle status={status}>
          {config.title}
          {!lgPri && config.showSparkle && <Icon.SparkleIcon />}
        </CosignButtonTitle>
        {config.subtitle && (
          <CosignButtonSubtitle status={status}>
            {config.subtitle}
          </CosignButtonSubtitle>
        )}
      </CosignButtonContent>
      {status !== 'pending' && (
        isCreatingCosignApplication ? (
          <CosignLoader status={status} />
        ) : (
          <CosignExternalIcon status={status}>
            <Icon.ExternalLinkIcon />
          </CosignExternalIcon>
        )
      )}
    </CosignActionButtonContainer>
  );
};
export const LumenSubtitle: React.FC<{
  showItems: boolean;
  subtitle: string;
  onClick: () => void;
}> = ({ subtitle, showItems, onClick }) => {
  const { isMobile } = useDeviceType();
  if (subtitle) return <Subtitle>{subtitle}</Subtitle>;
  if (showItems)
    return (
      <LumenSublabel strong disabled={!isMobile} onClick={onClick}>
        Lumen identified the below items:
      </LumenSublabel>
    );

  return null;
};

export const LumenIcon: React.FC<{ status: AppStatus; score?: string }> = ({
  status,
  score,
}) => {
  const props = {
    width: "40px",
    height: "40px",
  };
  if (status === AppStatus.Empty) {
    return <EmptyBoxWhite {...props} />;
  }
  if (status === AppStatus.Submitting) {
    return <CalculatingWhite {...props} />;
  }

  if (!score) return <EmptyBoxWhite {...props} />;
  return <Icon.BulbGraySmall {...props} />;
};

export const ActionButton: React.FC<{
  actions: Actions;
  permission: Permission;
  button?: ButtonType;
  lgPri?: boolean;
}> = ({
  button,
  permission = {},
  lgPri = false,
  actions: {
    cancel,
    approve,
    decline,
    conditional,
    conditionallyProceed,
    sendToPms,
  },
}) => {
    const { isViewOnly } = useIsViewOnlyUser();
    switch (button) {
      case ButtonType.approve:
        if (isViewOnly) return null;
        return (
          <Tooltip theme="light" title={permission.noApproveReason}>
            <div>
              {lgPri ? (
                <LgPriButton
                  type="success"
                  $verdict="approve"
                  onClick={approve}
                  disabled={permission.noApprove}
                >
                  Approve
                </LgPriButton>
              ) : (
                <ActionMainButton
                  type="success"
                  onClick={approve}
                  color={BaseColors.darkGreen}
                  disabled={permission.noApprove}
                >
                  Approve
                </ActionMainButton>
              )}
            </div>
          </Tooltip>
        );
      case ButtonType.conditional:
        if (isViewOnly) return null;
        return (
          <Tooltip
            theme="light"
            title={
              permission.noRequestInfoReason ||
              "Ask the applicant to submit additional requirements"
            }
          >
            <div>
              {lgPri ? (
                <LgPriButton
                  type="secondary"
                  $verdict="conditional"
                  onClick={conditional}
                  disabled={permission.noRequestInfo}
                >
                  Request More Info
                </LgPriButton>
              ) : (
                <ActionMainButton
                  type="secondary"
                  color={BaseColors.darkYellow}
                  backgroundColor={BaseColors.orange}
                  onClick={conditional}
                  disabled={permission.noRequestInfo}
                >
                  Request More Info
                </ActionMainButton>
              )}
            </div>
          </Tooltip>
        );
      case ButtonType.cancel:
        if (isViewOnly) return null;
        return (
          <Tooltip theme="light" title={permission.noCancelReason}>
            <div>
              {lgPri ? (
                <LgPriButton
                  type="primary"
                  $verdict="cancel"
                  onClick={cancel}
                  disabled={permission.noCancel}
                >
                  CANCEL DEAL
                </LgPriButton>
              ) : (
                <MoreActionsButton
                  type="primary"
                  onClick={cancel}
                  disabled={permission.noCancel}
                >
                  CANCEL DEAL
                </MoreActionsButton>
              )}
            </div>
          </Tooltip>
        );
      case ButtonType.decline:
        if (isViewOnly) return null;
        return (
          <Tooltip theme="light" title={permission.noDeclineReason}>
            <div>
              {lgPri ? (
                <LgPriButton
                  type="danger"
                  $verdict="decline"
                  onClick={decline}
                  disabled={permission.noDecline}
                >
                  Decline
                </LgPriButton>
              ) : (
                <ActionMainButton
                  color={BaseColors.darkRed}
                  type="danger"
                  onClick={decline}
                  disabled={permission.noDecline}
                >
                  Decline
                </ActionMainButton>
              )}
            </div>
          </Tooltip>
        );
      case ButtonType.conditionallyProceed:
        return (
          <Tooltip title={permission.noConditionalProceedReason} theme="light">
            <div>
              {lgPri ? (
                <LgPriButton
                  type="success"
                  $verdict="approve"
                  onClick={conditionallyProceed}
                  disabled={permission.noConditionalProceed}
                >
                  Conditionally Proceed
                </LgPriButton>
              ) : (
                <ActionMainButton
                  type="success"
                  color={BaseColors.darkGreen}
                  onClick={conditionallyProceed}
                  disabled={permission.noConditionalProceed}
                >
                  Conditionally Proceed
                </ActionMainButton>
              )}
            </div>
          </Tooltip>
        );
      case ButtonType.sendToPms:
        return (
          <Tooltip
            title={
              permission.canSendToPms
                ? ""
                : "The roles assigned to your user do not allow access to this functionality"
            }
            theme="light"
          >
            <div>
              {lgPri ? (
                <LgPriButton
                  type={permission.canSendToPms ? "success" : ""}
                  $verdict="approve"
                  onClick={sendToPms}
                  disabled={!permission.canSendToPms}
                >
                  Send to Entrata
                </LgPriButton>
              ) : (
                <ActionMainButton
                  type={permission.canSendToPms ? "success" : ""}
                  color={BaseColors.darkGreen}
                  onClick={sendToPms}
                  disabled={!permission.canSendToPms}
                >
                  Send to Entrata
                </ActionMainButton>
              )}
            </div>
          </Tooltip>
        );
      default:
        return null;
    }
  };

export const ActionPopover: React.FC<{
  cosignApplication?: any;
  primaryApplicantCosignBlockInfo?: {
    verifiedIncome?: number;
    rent?: number;
  };
  cosignEligibilityReasons?: string[];
  actions: Actions;
  buttons: ButtonType[];
  skip?: ButtonType;
  permission?: Permission;
  visiblePopover: boolean;
  onVisibleChange: (value: boolean) => void;
  lgPri?: boolean;
}> = ({
  cosignApplication,
  primaryApplicantCosignBlockInfo,
  cosignEligibilityReasons,
  actions: {
    cancel,
    conditional,
    decline,
    approve,
    conditionallyProceed,
    recalculateLumen,
    sendToPms,
    resendToPms,
    initiateCosign,
    isCreatingCosignApplication,
    downloadApprovalLetter,
  },
  buttons,
  children,
  skip,
  permission = {},
  visiblePopover,
  onVisibleChange,
  lgPri = false,
}) => {
    const { isViewOnly } = useIsViewOnlyUser();
    const { cancelCosignApplication } = useCancelCosignApplication();
    buttons = buttons
      .filter((b) => b !== skip)
      .filter((b) =>
        isViewOnly
          ? ![
            ButtonType.approve,
            ButtonType.cancel,
            ButtonType.decline,
            ButtonType.recalculateLumen,
          ].includes(b)
          : true
      );
    return (
      // @ts-ignore
      <Popover
        content={
          <PopoverContainer>
            {buttons.map((button) => {
              switch (button) {
                case ButtonType.approve:
                  return (
                    <Tooltip theme="light" title={permission.noApproveReason}>
                      <div>
                        <StyledActionButton
                          color={COLORS.deal.decisionDropdown.approve}
                          type="primary"
                          onClick={approve}
                          disabled={permission.noApprove}
                        >
                          Approve
                        </StyledActionButton>
                      </div>
                    </Tooltip>
                  );
                case ButtonType.conditionallyProceed:
                  return (
                    <Tooltip
                      theme="light"
                      title={permission.noConditionalProceedReason}
                    >
                      <div>
                        <StyledActionButton
                          color={COLORS.deal.decisionDropdown.approve}
                          type="primary"
                          onClick={conditionallyProceed}
                          disabled={permission.noConditionalProceed}
                        >
                          Conditionally Proceed
                        </StyledActionButton>
                      </div>
                    </Tooltip>
                  );

                case ButtonType.conditional:
                  return (
                    !isViewOnly && (
                      <Tooltip
                        theme="light"
                        title={
                          permission.noRequestInfoReason ||
                          "Ask the applicant to submit additional requirements"
                        }
                      >
                        <div>
                          <StyledActionButton
                            color={
                              COLORS.deal.decisionDropdown.conditionallyApproveV2
                            }
                            type="primary"
                            onClick={conditional}
                            disabled={permission.noRequestInfo}
                          >
                            Request More Info
                          </StyledActionButton>
                        </div>
                      </Tooltip>
                    )
                  );
                case ButtonType.decline:
                  return (
                    <Tooltip theme="light" title={permission.noDeclineReason}>
                      <div>
                        <StyledActionButton
                          color={COLORS.deal.decisionDropdown.decline}
                          type="primary"
                          onClick={decline}
                          disabled={permission.noDecline}
                        >
                          Decline Application
                        </StyledActionButton>
                      </div>
                    </Tooltip>
                  );
                case ButtonType.cancel:
                  return (
                    <Tooltip theme="light" title={permission.noCancelReason}>
                      <div>
                        <StyledActionButton
                          type="primary"
                          onClick={cancel}
                          disabled={permission.noCancel}
                        >
                          Cancel Application
                        </StyledActionButton>
                      </div>
                    </Tooltip>
                  );
                case ButtonType.recalculateLumen:
                  return (
                    <StyledActionButton
                      type="primary"
                      onClick={recalculateLumen}
                      color={colors.secondary[100]}
                    >
                      Recalculate Lumen
                    </StyledActionButton>
                  );
                case ButtonType.sendToPms:
                  return (
                    <StyledActionButton
                      color={COLORS.deal.decisionDropdown.approve}
                      onClick={sendToPms}
                      disabled={!permission.canSendToPms}
                      type="primary"
                    >
                      Send to Entrata
                    </StyledActionButton>
                  );
                case ButtonType.resendToPms:
                  return (
                    <StyledActionButton
                      color={COLORS.deal.decisionDropdown.approve}
                      onClick={resendToPms}
                      type="primary"
                    >
                      Resend to PMS
                    </StyledActionButton>
                  );
                case ButtonType.initiateCosign:
                  // TODO: Let them reapply to cosign  in phase-2
                  // if (["denied", "canceled", "expired"].includes(cosignApplication?.status)) {
                  //   return null;
                  // }
                  
                  const COSIGN_REASON_LABELS: Record<string, string> = {
                    income_below_cosign_minimum: 'Income must be at least 2x monthly rent',
                    missing_lease_start_date: 'Lease start date is required',
                    missing_unit_assignment: 'A unit must be assigned to this deal',
                    lease_term_below_cosign_minimum: 'Lease term must be at least 3 months',
                  };
                  const SOFT_BLOCKER_REASONS = new Set(['income_below_cosign_minimum', 'missing_lease_start_date', 'missing_unit_assignment', 'lease_term_below_cosign_minimum']);
                  const softBlockerReasons = (cosignEligibilityReasons || []).filter((r) => SOFT_BLOCKER_REASONS.has(r));
                  const hasSoftBlockers = cosignApplication?.status === undefined && softBlockerReasons.length > 0;
                  const shouldDisable = hasSoftBlockers;
                  const tooltipMessage = hasSoftBlockers ? (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Cosign eligibility requirements not met.</div>
                      <div style={{ marginBottom: 8, fontSize: 12 }}>Cosign sets underwriting requirements that must be met before guarantor coverage can be offered.</div>
                      <ul style={{ paddingLeft: 18, margin: '0 0 8px', fontSize: 12 }}>
                        {softBlockerReasons.map((reason) => (
                          <li key={reason}>{COSIGN_REASON_LABELS[reason] || reason}</li>
                        ))}
                      </ul>
                      <div style={{ fontSize: 12 }}>Update the application details to meet Cosign requirements.</div>
                    </div>
                  ) : "";
                  
                  return (
                    <Tooltip title={tooltipMessage}>
                      <CosignActionButton
                        lgPri={lgPri}
                        disabled={shouldDisable || isCreatingCosignApplication}
                        status={cosignApplication?.status as CosignStatus}
                        isCreatingCosignApplication={isCreatingCosignApplication}
                        onClick={() => {
                          if (isCreatingCosignApplication) return;
                          switch (cosignApplication?.status) {
                            case 'approved':
                              // Do download cosign application
                              downloadApprovalLetter({ cosignUuid: cosignApplication?.cosignUuid });
                              break;
                            case 'pending':
                              cancelCosignApplication(cosignApplication?.cosignUuid);
                              onVisibleChange(false); // Close popover after canceling
                              break;
                            case undefined:
                              initiateCosign();
                              break;
                            case 'denied':
                              // Do nothing
                              initiateCosign();
                              break;
                            case 'canceled':
                              // Do nothing
                              initiateCosign();
                              break;
                            case 'expired':
                              // Do nothing
                              initiateCosign();
                              break;
                            default:
                              // Nothing for v1
                              break;
                          }
                        }}
                      />
                      <div style={{ height: '6px' }} /></Tooltip>
                  );

                default:
                  return null;
              }
            })}
          </PopoverContainer>
        }
        trigger="click"
        placement="bottomRight"
        withoutOverlay
        overlayClassName={lgPri ? "dark-theme lg-menu" : "dark-theme"}
        visible={visiblePopover}
        onVisibleChange={onVisibleChange}
      >
        {!buttons?.length ? null : children}
      </Popover>
    );
  };
